/**
 * Module dependencies.
 */

import React, { useEffect, useState } from 'react';
import { cn } from 'app/lib/utils';
import { chartPalette } from 'app/components/organisms/charts/charts';
import { resolveAppUrl } from 'app/core/utils/url-resolver';

/**
 * Deterministic palette colour for a symbol (fallback badge).
 */

function colorFor(symbol: string): string {
  let hash = 0;

  for (let i = 0; i < symbol.length; i++) {
    hash = (hash + symbol.charCodeAt(i)) % chartPalette.length;
  }

  return chartPalette[hash];
}

/**
 * Our self-hosted, cached logo endpoint for a symbol. The API resolves the icon
 * from public sources once and serves it thereafter, so the client depends only
 * on our own origin (and the browser HTTP cache) — not the upstream CDNs.
 */

function logoUrl(symbol: string): string {
  return resolveAppUrl('cryptoGestAssetLogo', { interpolations: { symbol: symbol.toUpperCase() } });
}

/**
 * Export `AssetIcon` — the asset's real logo (self-hosted + cached), or a tinted
 * ticker badge when no icon could be resolved.
 */

export function AssetIcon({
  symbol,
  className
}: {
  symbol?: string | null;
  // Kept for call-site compatibility; the cached endpoint resolves the icon.
  logo?: string | null;
  className?: string;
}): JSX.Element {
  const [failed, setFailed] = useState(false);

  // Reset when the symbol changes (rows reused across renders).
  useEffect(() => {
    setFailed(false);
  }, [symbol]);

  const ticker = (symbol ?? '?').slice(0, 3).toUpperCase();

  if (symbol && !failed) {
    return (
      <img
        alt={symbol}
        className={cn('h-6 w-6 shrink-0 rounded-full bg-white object-contain', className)}
        onError={() => setFailed(true)}
        src={logoUrl(symbol)}
      />
    );
  }

  const color = colorFor(ticker);

  return (
    <span
      className={cn('grid h-6 w-6 shrink-0 place-items-center rounded-full text-[9px] font-semibold', className)}
      style={{ backgroundColor: `${color}22`, color }}
    >
      {ticker}
    </span>
  );
}
